import moment from 'moment-timezone';
import {
  APP_URL_LOCAL,
  APP_URL_PRODUCTION,
  CURRENCY,
  DATE_FORMAT,
  DEFAULT_LANGUAGE,
} from '../config';
import en from '../translations/en.json';
import tc from '../translations/tc.json';
import { translationKey } from '../types/common.types';
import { useState, useEffect } from 'react';

function getAppUrl() {
  return window.location.hostname === 'localhost'
    ? APP_URL_LOCAL
    : APP_URL_PRODUCTION;
}

function isFullAppUrl() {
  const currentUrl = window.location.hostname;
  const fullAppUrl = [
    APP_URL_PRODUCTION.replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .replace(/:\d+$/, ''),
    APP_URL_LOCAL.replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .replace(/:\d+$/, ''),
  ];
  return fullAppUrl.includes(currentUrl);
}

const DICTIONARIES: Record<string, Record<string, string>> = { en, tc };

function getLanguage() {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (fromQuery) {
    return fromQuery;
  }

  // The form is also embedded in the WordPress site, where the language lives
  // in the path (/en/...) instead of a query string.
  return /^\/en(\/|$)/.test(window.location.pathname) ? 'en' : DEFAULT_LANGUAGE;
}

/**
 * Carry the active language across navigation. The language lives in the URL
 * only, so a plain link or redirect would silently drop it back to Chinese.
 */
function withLanguage(path: string): string {
  const lang = getLanguage();
  if (lang === DEFAULT_LANGUAGE) {
    return path;
  }

  const [base, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set('lang', lang);
  return `${base}?${params.toString()}`;
}

function __(text: translationKey, ...args: any[]): string {
  // Handle null, undefined, or empty inputs
  if (text === null || text === undefined || text === '') {
    return '';
  }

  // Convert input to string if it's not already
  const inputText = typeof text === 'string' ? text : String(text);

  const lang = getLanguage();
  const normalizedText = inputText.toLowerCase();

  // Find the translation. Keys are the English source strings, so an entry is
  // only needed where the rendered text differs from the key itself.
  let translated = inputText;
  const dictionary = DICTIONARIES[lang];
  if (dictionary) {
    // Create a case-insensitive lookup map if not already cached
    const matchingKey = Object.keys(dictionary).find(
      (key) => key.toLowerCase() === normalizedText
    );

    if (matchingKey) {
      translated = dictionary[matchingKey];
    }
  }

  // Replace placeholders with arguments
  if (args.length > 0) {
    let translatedStr = String(translated);

    // Process each argument
    args.forEach((arg, index) => {
      const placeholder = `$${index + 1}`;
      const argValue = arg === null || arg === undefined ? '' : String(arg);

      // Replace all occurrences of the placeholder
      translatedStr = translatedStr.split(placeholder).join(argValue);
    });

    translated = translatedStr;
  }

  return translated;
}

// This is a custom React hook that follows the naming convention
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// This is a utility function for use outside of React components
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

function formatPrice(
  price: number | undefined,
  symbol: string = CURRENCY,
  decimalPlaces: number = 0
) {
  if (!price) return null;

  return (
    symbol + price.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
}

function formatDate(date: string | Date | null | undefined) {
  return moment(date).format(DATE_FORMAT);
}

/** Customers enter their own phone number. Backend trims and caps at 12. */
export const REFERRAL_CODE_MAX_LENGTH = 12;

export function normalizeReferralCode(
  value: string | null | undefined
): string {
  return (value ?? '').trim().slice(0, REFERRAL_CODE_MAX_LENGTH);
}

export {
  __,
  getLanguage,
  withLanguage,
  useDebounce,
  debounce,
  formatPrice,
  formatDate,
  getAppUrl,
  isFullAppUrl,
};
